import React, { createContext, useEffect, useContext, useState } from "react";
import { tripAPI, variantAPI, elementAPI } from "../../api/apiClient";
import useApi from "../CustomHooks/useApi";

const TripContext = createContext();

export const TripProvider = ({ children, guid }) => {
  const [tripData, setTripData] = useState(null);
  const [isFullData, setIsFullData] = useState(null);
  const [hasExplicitAccess, setHasExplicitAccess] = useState(null);
  const [showVariantInForm, setShowVariantInForm] = useState(false);
  const [isTripError, setIsTripError] = useState(false);
  const [selectedVariantGuid, setSelectedVariantGuid] = useState(null);
  const { callApi, isLoading: loading, error } = useApi();

  const fetchTripData = async () => {
    if (!guid) {
      setTripData(null);
      setIsTripError(true);
      return;
    }
    setIsTripError(false); 
    const data = await callApi(() => tripAPI.getTrip(guid));
    if (data) {
      setTripData(data.trip);
      setIsFullData(data.isFullData);
      setHasExplicitAccess(data.hasExplicitAccess)
    } else {
      setTripData(null);
      setIsTripError(true);
    }
  };

    const fetchTripFullData = async (reason = null) => {
    if (!guid) {
      setTripData(null);
      setIsTripError(true);
      return;
    }

    setIsTripError(false);
    const data = await callApi(() => tripAPI.getTripFull(guid, reason));

    if (data) {
      setTripData(data.trip);
      setIsFullData(data.isFullData);
      setHasExplicitAccess(data.hasExplicitAccess)
    } else {
      setIsTripError(true);
    }
  };


  useEffect(() => {
    fetchTripData();
  }, [guid]);

  const updateTripDataByObject = (object) => {
    setTripData((prev) => ({ ...prev, ...object }));
  };

const handleApiCall = async (apiFunc, ...args) => {
  const success = await callApi(() => apiFunc(...args));
  if (success !== undefined) {
    await fetchTripData();
  }
};

  const deleteVariant = async (variantGuid) => {
    await handleApiCall(variantAPI.deleteVariant, variantGuid);
  };

  const addElementToVariant = async (variantGuid, elementDataToAdd) => {
    await handleApiCall(elementAPI.createElement, variantGuid, elementDataToAdd);
  };

  const updateElement = async (elementData) => {
    await handleApiCall(elementAPI.updateElement, elementData);
  };

  const linkElementWithVariant = async (elementGuid, variantGuid) => {
    await handleApiCall(elementAPI.linkElementWithVariant, elementGuid, variantGuid);
  };

  const deleteElementFromVariant = async (elementGuid, variantGuid) => {
    await handleApiCall(elementAPI.deleteElementFromVariant, elementGuid, variantGuid);
  };

  const addVariant = async (tripGuid, variantData) => {
    await handleApiCall(variantAPI.createVariant, tripGuid, variantData);
  };

  const updateVariant = async (variantData) => {
    await handleApiCall(variantAPI.updateVariant, variantData);
  };

  const updateTripBaseInfo = async (tripBaseInfo) => {
    await handleApiCall(tripAPI.updateTrip, tripBaseInfo);
  };



  // const addCorrectionsToParticipant = (participantGuid, newCorrections) => {
  //   setTripData((prev) => {
  //     const updatedRegistrations = prev.registrations.map((reg) => {
  //       const updatedParticipants = reg.participants.map((p) => {
  //         if (p.guid === participantGuid) {
  //           const addedAmount = newCorrections.reduce((sum, c) => sum + c.amount, 0);
  //           return {
  //             ...p,
  //             corrections: [...(p.corrections || []), ...newCorrections],
  //             totalCost: (p.totalCost || 0) + addedAmount
  //           };
  //         }
  //         return p;
  //       });

  //       const updatedReg = { ...reg, participants: updatedParticipants };
  //       const newAmountToPay = recalculateAmountToPay(updatedReg);
  
  //       return { ...updatedReg, amountToPay: newAmountToPay };
  //     });
  
  //     return { ...prev, registrations: updatedRegistrations };
  //   });
  // };

  // const removeCorrectionsFromParticipant = (participantGuid, correctionGuids) => {
  //   setTripData((prev) => {
  //     const updatedRegistrations = prev.registrations.map((reg) => {
  //       const updatedParticipants = reg.participants.map((p) => {
  //         if (p.guid === participantGuid) {
  //           const toRemove = (p.corrections || []).filter(c => correctionGuids.includes(c.guid));
  //           const removedAmount = toRemove.reduce((sum, c) => sum + c.amount, 0);
  //           return {
  //             ...p,
  //             corrections: (p.corrections || []).filter(c => !correctionGuids.includes(c.guid)),
  //             totalCost: (p.totalCost || 0) - removedAmount
  //           };
  //         }
  //         return p;
  //       });
  
  //       const updatedReg = { ...reg, participants: updatedParticipants };
  //       const newAmountToPay = recalculateAmountToPay(updatedReg);

  //       return { ...updatedReg, amountToPay: newAmountToPay };
  //     });
  
  //     return { ...prev, registrations: updatedRegistrations };
  //   });
  // };

  const updateParticipantInContext = (updatedParticipant) => {
    setTripData((prev) => {
      const updatedRegistrations = prev.registrations.map((reg) => {
        const updatedParticipants = reg.participants.map((p) =>
          p.guid === updatedParticipant.guid ? updatedParticipant : p
        );
        return { ...reg, participants: updatedParticipants };
      });
  
      return { ...prev, registrations: updatedRegistrations };
    });
  };

  const updateRegistrationInContext = (updatedRegistration) => {
    setTripData((prev) => {
      const updatedRegistrations = prev.registrations.map((reg) =>
        reg.reservationGuid === updatedRegistration.reservationGuid ? updatedRegistration : reg
      );
      return { ...prev, registrations: updatedRegistrations };
    });
  };

  const updateParticipantsBulkInContext = (updates) => {
    setTripData((prevData) => {
      if (!prevData) return prevData;
  
      const updatedRegistrations = prevData.registrations.map((registration) => {
        const updatedParticipants = registration.participants.map((p) => {
          const update = updates.find((u) => u.guid === p.guid);
          return update ? { ...p, ...update } : p;
        });
  
        return {
          ...registration,
          participants: updatedParticipants,
        };
      });
  
      return {
        ...prevData,
        registrations: updatedRegistrations,
      };
    });
  };

  const updateParticipantsDiscordInContext = (updates) => {
    setTripData((prevData) => {
      if (!prevData) return prevData;
  
      const updatedRegistrations = prevData.registrations.map((registration) => {
        const updatedParticipants = registration.participants.map((p) => {
          const update = updates.find((u) => u.guid === p.guid);
          return update ? { ...p, isDiscordUser: update.isDiscordUser } : p;
        });
  
        return {
          ...registration,
          participants: updatedParticipants,
        };
      });
  
      return {
        ...prevData,
        registrations: updatedRegistrations,
      };
    });
  };

  const updateTripStatus = (newStatus) => {
    setTripData((prev) => ({
      ...prev,
      status: newStatus
    }));
  };
  
  


  const contextValue = {
    tripData,
    loading,
    error: isTripError,
    isFullData,
    hasExplicitAccess,
    fetchTripData,
    fetchTripFullData,
    updateTripDataByObject,
    updateTripBaseInfo,
    addElementToVariant,
    deleteVariant,
    deleteElementFromVariant,
    linkElementWithVariant,
    addVariant,
    updateVariant,
    updateElement,
    showVariantInForm,
    setShowVariantInForm,
    selectedVariantGuid,
    setSelectedVariantGuid,
    updateParticipantInContext,
    updateRegistrationInContext,
    updateParticipantsBulkInContext,
    updateParticipantsDiscordInContext,
    updateTripStatus
  };

  return <TripContext.Provider value={contextValue}>{children}</TripContext.Provider>;
};

export const useTripContext = () => useContext(TripContext);
